import React, { useCallback, useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import Backend from "../../../../Backend/Backend";

// Initialize backend once (moved outside component to avoid re-initialization)
let backendInstance = null;
const getBackend = async () => {
  if (!backendInstance) {
    backendInstance = await new Backend().init();
  }
  return backendInstance;
};

export default function ActionButton({ label, item, user, reload, userType }) {
  const [newValue, setNewValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);
  const backendRef = useRef(null);

  // Initialize backend on mount
  useEffect(() => {
    getBackend().then((backend) => {
      backendRef.current = backend;
    });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ✅ Config map for field-to-payload mapping
  const fieldMap = {
    sq_time: {
      field: "sq_time",
      flag: "time",
      successMsg: "SQ time updated",
    },
    limit_p: {
      field: "limit_p",
      flag: "limit_p",
      successMsg: "Limit % updated",
    },
    profit_limit: {
      field: "pl_limit",
      flag: "plimit",
      successMsg: "Profit limit updated",
    },
    standing_limit: {
      field: "s_limit",
      flag: "slimit",
      successMsg: "Standing limit updated",
    },
    min_rate: {
      field: "min_rate",
      flag: "min_rate",
      successMsg: "Min rate updated",
    },
    max_lot: {
      field: "total_mcx_lot",
      flag: "mcx_lot",
      successMsg: "MCX lot updated",
    },
  };

  // Generic update function (merged from old code)
  const genericUpdate = useCallback(
    async (fieldName, payload, successMsg) => {
      // Permission check from old code
      if (userType === 5 || userType === 6) {
        return toast.error("Permission Not Allowed.");
      }

      if (!backendRef.current) return toast.error("Backend not ready");

      setIsLoading(true);

      try {
        const base = {
          server: localStorage.getItem("server"),
          id: localStorage.getItem("id"),
          username: localStorage.getItem("username"),
          token: localStorage.getItem("token"),
          user_id: user.id,
        };

        const finalPayload = { ...base, ...payload };
        const response = await backendRef.current[fieldName](finalPayload);

        if (!mountedRef.current) return;

        if (response && response.error === "False") {
          toast.success(successMsg || response.message || "Updated");
          reload?.();
          return true;
        } else {
          toast.error(response?.message || "Update failed");
          return false;
        }
      } catch (err) {
        console.error(err);
        toast.error("Error while updating");
        return false;
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [userType, user, reload]
  );

  // Specific update handlers (merged approach)
  const handleSave = useCallback(async () => {
    if (!newValue.trim()) {
      toast.error("Value cannot be empty");
      return;
    }

    const config = fieldMap[item];
    if (!config) {
      toast.error("Unknown field update attempted");
      return;
    }

    const payload = {
      [config.flag]: newValue,
    };

    const success = await genericUpdate(
      config.field,
      payload,
      config.successMsg
    );

    if (success) {
      setNewValue(""); // reset input on success
    }
  }, [newValue, item, genericUpdate]);

  return (
    <div className="flex items-center gap-2 py-1">
      <input
        type="text"
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        disabled={isLoading}
        className="w-[100px] border-1 text-[var(--text)] bg-[var(--background)] border-[var(--hover)] rounded-lg px-2 py-1 text-xs font-semibold shadow-sm focus:ring-2 focus:ring-[var(--primary)] disabled:opacity-50"
        placeholder="Enter value"
      />
      <button
        onClick={handleSave}
        disabled={isLoading || !newValue.trim()}
        className={`${"bg-[var(--primary)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)] disabled:opacity-50`}
      >
        {isLoading ? "Saving" : label}
      </button>
    </div>
  );
}
